'use client';
import ProductAdminControls from '@c/admin/ProductAdminControls';
import { SoldOverlay } from '@c/dashboard/pages/ListingsPage';
import BikeDetails from '@c/dirt-bikes/BikeDetails';
import PriceGuideChart from '@c/dirt-bikes/PriceGuideChart';
import ProductImages from '@c/dirt-bikes/ProductImage';
import EstimateDirtBikeShippingModal from '@c/modals/EstimateDirtBikeShippingModal';
import ReportItemModal from '@c/modals/ReportItemModal';
import ProductDescription from '@c/product-details/ProductDescription';
import ProductPriceAndShipping from '@c/product-details/ProductPriceAndShipping';
import SellerInfo from '@c/product-details/SellerInfo';
import { FlagIcon } from '@heroicons/react/20/solid';
import { useQuery } from '@tanstack/react-query';
import Breadcrumb, { BreadcrumbItem } from '@ui/Breadcrumb';
import Button from '@ui/Button';
import Carousel from '@ui/Carousel';
import SafeImage from '@ui/SafeImage';
import { viewContent } from '@util/analytics';
import { getPublicUserDoc } from '@util/firestore/users';
import useIsClient from '@util/hooks/useIsClient';
import { isMobile } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { renderProductCards } from 'features/homepage';
import HomeSection from 'features/homepage/components/Home/components/HomeSection';
import {
  injectBikeProductScript,
  injectBreadcrumbScript,
} from 'features/schemaMarkups';
import { ProductDocument } from 'models/product';
import { isBot } from 'next/dist/server/web/spec-extension/user-agent';
import { useEffect, useState } from 'react';

interface ProductDetailPageProps {
  product: ProductDocument;
  breadcrumbs: BreadcrumbItem[];
  parts: ProductDocument[];
  isPreview?: boolean;
}

export default function ProductDetailPage(props: ProductDetailPageProps) {
  const { product, breadcrumbs, parts } = props;
  const { userDoc } = useAuth();
  const [openModal, setOpenModal] = useState<
    'reportItem' | 'estimateShipping' | ''
  >('');

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (typeof (window as any).fbq === 'function') {
        viewContent(product, 'product', userDoc?.uid ?? '');
        clearInterval(intervalId); // Stop polling when fbq is available
      }
    }, 1000); // Check every second

    return () => clearInterval(intervalId); // Clean up interval on unmount
  }, [product, userDoc?.uid]);

  const isClient = useIsClient();

  const { data: seller } = useQuery({
    queryKey: ['publicUser', product.seller_id],
    queryFn: () => getPublicUserDoc({ uid: product.seller_id }),
    enabled: !!product.seller_id,
  });

  function GMapCircle(p: ProductDocument, isMobile: boolean) {
    if (typeof window === 'undefined') return;
    if (isBot(window.navigator.userAgent)) return;
    let lat = p.location?.latitude;
    let lng = p.location?.longitude;

    if (!lat || !lng) return;

    const api_key = process.env.NEXT_PUBLIC_FIREBASE_API_KEY;
    const uri = `https://maps.googleapis.com/maps/api/staticmap?key=${api_key}`;
    const bikeColors: { [make: string]: string } = {
      Beta: 'e60000',
      Cobra: 'ffe013',
      'Gas Gas': 'cc0002',
      Honda: 'c80b0b',
      Husqvarna: '143761',
      Kawasaki: '32fe00',
      KTM: 'f27620',
      Sherco: '#0806a9',
      Suzuki: 'f5f809',
      Yamaha: '003993',
    };

    // Don't want to give away exact location :)
    lat = +lat.toFixed(1);
    lng = +lng.toFixed(1);
    const rad = 20000;
    var staticMapSrc = '&center=' + lat + ',' + lng;
    staticMapSrc += '&zoom=7';
    const size = isMobile ? '300x300' : '1100x200';
    staticMapSrc += '&size=' + size;
    if (!isMobile) staticMapSrc += '&scale=2';
    const color = bikeColors[p.make!] ?? 'c80b0b';
    staticMapSrc += `&path=color:0x${color}|fillcolor:0x${color}|weight:0`;

    var r = 6371;
    var pi = Math.PI;
    var _lat = (lat * pi) / 180;
    var _lng = (lng * pi) / 180;
    var d = rad / 1000 / r;
    var i = 0;
    for (i = 0; i <= 360; i += 8) {
      var brng = (i * pi) / 180;
      var pLat = Math.asin(
        Math.sin(_lat) * Math.cos(d) +
          Math.cos(_lat) * Math.sin(d) * Math.cos(brng)
      );
      var pLng =
        ((_lng +
          Math.atan2(
            Math.sin(brng) * Math.sin(d) * Math.cos(_lat),
            Math.cos(d) - Math.sin(_lat) * Math.sin(pLat)
          )) *
          180) /
        pi;
      pLat = (pLat * 180) / pi;
      staticMapSrc += '|' + pLat + ',' + pLng;
    }

    return uri + encodeURI(staticMapSrc);
  }

  const mapSrc = GMapCircle(product, isMobile());

  return (
    <>
      <script
        id="schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: injectBikeProductScript(product) ?? '',
        }}
      />
      <script
        id="breadcrumbSchema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: injectBreadcrumbScript({ breadcrumbs }) ?? '',
        }}
      />
      <div
        className={`contain-layout ${
          props.isPreview ? 'pointer-events-none cursor-not-allowed' : ''
        }`}
      >
        {(userDoc?.roles?.support || userDoc?.roles?.admin) && (
          <ProductAdminControls product={product} />
        )}

        <div className="flex w-full flex-col items-center">
          <div className="relative flex w-full max-w-[150rem] justify-center ">
            <ProductImages product={product} />
            {product.out_of_stock && !product.is_auction && (
              <SoldOverlay variant="sold" />
            )}
          </div>

          <div className="lg:px[0rem] w-[50rem] px-[1.6rem] sm:mx-auto sm:w-full sm:max-w-[120rem] md:px-[5rem]">
            <div className="my-[3.2rem] w-full">
              <Breadcrumb items={breadcrumbs} wrap />
            </div>

            <div className="flex w-full flex-col gap-[4rem] px-8 sm:max-w-[120rem] sm:gap-[6.4rem] sm:px-0 lg:flex-row">
              <div className="w-full lg:w-2/3">
                <h1 className="mb-[1.6rem] text-h2 font-semibold leading-[4rem]">
                  {product.title}
                </h1>
                <div className="mb-[2.4rem] flex flex-col items-start gap-[1.6rem] xl:flex-row">
                  <ProductPriceAndShipping product={product} />
                </div>
                {userDoc?.roles?.admin && (
                  <>
                    <Button
                      text="Estimate Shipping"
                      onClick={() => setOpenModal('estimateShipping')}
                    />
                    <EstimateDirtBikeShippingModal
                      isOpen={openModal === 'estimateShipping'}
                      dismiss={() => setOpenModal('')}
                      product_id={product.id}
                    />
                  </>
                )}
                <BikeDetails product={product} />

                <div className="mt-8">
                  <ProductDescription product={product} hideReadMore />
                </div>
              </div>

              <div className="w-full lg:w-1/3 ">
                <SellerInfo product={product} seller={seller} />
                <Button
                  text="Report this item"
                  type="text"
                  width="small"
                  onClick={() => {
                    setOpenModal('reportItem');
                  }}
                  leadingIcon={
                    <FlagIcon
                      fill="white"
                      stroke="black"
                      strokeWidth={2}
                      width={24}
                      height={24}
                    />
                  }
                />
                {openModal === 'reportItem' && (
                  <ReportItemModal
                    isUser={false}
                    itemId={product.id}
                    isOpen={openModal === 'reportItem'}
                    dismiss={() => setOpenModal('')}
                  />
                )}
              </div>
            </div>
            <div className="mt-[3.2rem] ">
              {mapSrc && isClient && (
                <SafeImage
                  src={mapSrc}
                  key={mapSrc}
                  alt="location"
                  classes="w-full max-h-[40rem] "
                />
              )}

              <p className="mt-[1.6rem] text-[1.4rem] text-brand-extreme-lightest-black">
                *Map is approximate to keep the seller&apos;s location private.
              </p>
            </div>

            <PriceGuideChart product={product} />
          </div>
          <div className="mx-auto mt-16 w-[50rem] px-2 sm:w-full sm:max-w-[160rem] sm:text-left">
            <HomeSection title="Parts for this bike">
              <Carousel
                items={renderProductCards(parts, {
                  data_source: 'firebase',
                  page: 'dirt-bikes-for-sale',
                  section: 'dirt-bikes-for-sale-parts',
                })}
              />
            </HomeSection>
          </div>
        </div>
      </div>
    </>
  );
}
